import { create } from "zustand"
import { devtools, persist } from "zustand/middleware"
import { APP_ID } from "../utils/constants"
import { request } from "utils"
import { useAuthStore } from "./auth"

export interface ProfileCredentialsInterface {
  email: string
  lastName: string
  firstName: string
  othername: string
  mobileNumber: string
  password: string
}

export interface BioDataCredentialsInterface {
  lastName: string
  firstName: string
  othername: string
  mobileNumber: string
  address: string
  nationality: string
  state: string
  maritalStatus: string
  lga: string
  gender: string
  dob: string
}

export interface NextOfKinCredentialsInterface {
  lastName: string
  firstName: string
  othername: string
  email: string
  mobileNumber: string
  gender: string
  occupation: string
  relationship: string
  address: string
  lga: string
  applicationID?: string
}

export interface ActivateProfileCredentialsInterface {
  verificationCode: string
}

export interface PictureUploadInterface {
  profilePicture: string
}

export enum stages {
  ProfileInitiation = "ProfileInitiation",
  ProfileActivation = "ProfileActivation",
  PaymentInitiation = "PaymentInitiation",
  PaymentCompletion = "PaymentCompletion",
  Biodata = "Biodata",
  NextOfKin = "NextOfKin",
  ProgrammeApplication = "ProgrammeApplication",
  EducationalRecord = "EducationalRecord",
  OlevelResult = "OlevelResult",
  EmploymentRecord = "EmploymentRecord",
  PictureUpload = "PictureUpload",
  SubmitApplication = "SubmitApplication",
  ApplicationSumitted = "ApplicationSumitted",
  Admitted = "IsAdmitted",
  rejected = "IsRejected",
}

export enum stageLevel {
  ProfileInitiation = 1,
  ProfileActivation = 2,
  PaymentInitiation = 3,
  PaymentCompletion = 4,
  Biodata = 5,
  NextOfKin = 6,
  ProgrammeApplication = 7,
  EducationalRecord = 8,
  OlevelResult = 9,
  EmploymentRecord = 10,
  PictureUpload = 11,
  SubmitApplication = 12,
  ApplicationSumitted = 13,
  IsAdmitted = 14,
  IsRejected = 15,
}

export interface ProfileInterface {
  profileId: any
  applicationNumber: string
  surname: string
  firstname: string
  othername: string
  address: string
  nationality: string
  programmeId: any
  state: string
  lga: string
  dob: string
  mobileNo: string
  email: string
  gender: string
  maritalStatus: string
  nextName: string
  nextEmail: string
  nextPhoneNumber: string
  nextAddress: string
  nextGender: string
  nextOccupation: string
  formNumber: number
  currentStage: stages
  nextStage: stages
  avatar: string
  relationship: string
  lastLogin: string
  department: string
  programmeLevel: string
  hasPaidAcceptanceFee: boolean
  hasPaidSchoolFee: boolean
}

interface ProfileState {
  profile?: ProfileInterface
  matric?: any
}

interface ProfileMethods {
  getProfile: () => Promise<any>
  getMatric: () => Promise<any>
  createProfile: (credential: ProfileCredentialsInterface) => Promise<any>
  updateProfilePicture: (credentials: PictureUploadInterface) => Promise<any>
  updateBioData: (credential: BioDataCredentialsInterface) => Promise<any>
  createPortalAccount: (credential: { portalPassword: string }) => Promise<any>
  processNextOfKin: (credential: NextOfKinCredentialsInterface) => Promise<any>
  printAdmissionLetter: () => Promise<any>
  activateProfile: (
    credential: ActivateProfileCredentialsInterface
  ) => Promise<any>
}

export const useProfileStore = create<ProfileState & ProfileMethods>()(
  devtools(
    persist(
      (set, get) => ({
        profile: undefined,
        getProfile: async () => {
          console.log("callign get proifle!!!")
          return await request
            .get({
              url: "/api/Profile/Get-Profile",
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              if (resp.isSuccessful === true) {
                set({
                  profile: resp.data,
                })
              }
              return
            })
        },
        matric: undefined,
        getMatric: async () => {
          return await request
            .get({
              url: "/api/Profile/Get-MatricNumber",
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              if (resp.isSuccessful === true) {
                set({
                  matric: resp.message || "",
                })
              }
              return
            })
        },
        createProfile: async (credential: ProfileCredentialsInterface) => {
          return await request.post({
            url: "/api/Profile/Create-Short-Profile",
            body: credential,
          })
        },
        createPortalAccount: async (credential: { portalPassword: string }) => {
          return await request.post({
            url: "/api/Profile/create-portal-account",
            body: credential,
            headers: { ...useAuthStore.getState().getAuthHeader() },
          })
        },
        updateProfilePicture: async (credentials: PictureUploadInterface) => {
          return await request
            .post({
              url: "/api/Profile/save-profile-picture",
              body: credentials,
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then((resp) => {
              if (resp.isSuccessful === true) {
                set({
                  profile: {
                    ...get().profile!,
                    nextStage: stages.SubmitApplication,
                  },
                })
              }
              return resp
            })
        },
        updateBioData: async (credential) => {
          return request
            .patch({
              url: "/api/Profile/update-biodata",
              body: credential,
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then(async (resp) => {
              if (resp.isSuccessful === true) {
                await get().getProfile()
              }
              return resp
            })
        },
        processNextOfKin: async (credential) => {
          return await request
            .post({
              url: "/api/Profile/process-nextofkin",
              body: credential,
              headers: { ...useAuthStore.getState().getAuthHeader() },
            })
            .then(async (resp) => {
              if (resp.isSuccessful === true) {
                await get().getProfile()
              }
              return resp
            })
        },
        printAdmissionLetter: async () => {
          return await request
            .get(
              {
                url: "/api/Printing/print-admission-letter",
                headers: { ...useAuthStore.getState().getAuthHeader() },
              },
              {
                responseType: "blob", // Important: responseType should be 'blob' to handle binary data
              }
            )
            .then(async (resp) => {
              if (resp?.isSuccessful === false) {
                return
              } else {
                console.log(resp)
                const downloadUrl = window.URL.createObjectURL(new Blob([resp]))
                const link = document.createElement("a")
                link.href = downloadUrl
                link.setAttribute("download", "letterofAdmission.pdf") // Specify the file name
                document.body.appendChild(link)
                link.click()
              }
              return resp
            })
        },
        activateProfile: async (credential) => {
          return await request
            .post({
              url: "/api/Profile/activate-profile",
              body: credential,
            })
            .then(async (resp) => {
              return resp
            })
        },
      }),
      {
        name: `${APP_ID}.profile`,
      }
    )
  )
)
